import { DefaultAsyncSourceSpanModel } from 'components/AsyncSourceSpan/AsyncSourceSpanModel';
import { AbstractCustomInputComponentModel } from 'components/CampaignLimitation';
import { ItemType } from 'components/CampaignLimitation/SelectItemComponent';
import { Limitation } from 'core/limitation/Limitation';
import { OPERATE, OPERATES } from 'enum/Operate';
import { AggregatedSpaceHealth } from 'utils/spaceHealth';
import { ElementType } from 'react';

export const DEFAULT_INVENTORY = 'default';

export enum LIMITATION_TYPE {
  CAMPAIGN = 'campaign',
  CREATIVE = 'creative'
}

export const operateTitles = {
  [OPERATE.INCLUDE]: {
    0: 'limitation.labels.incNoValue',
    1: 'limitation.labels.incHaveValue'
  },
  [OPERATE.EXCLUDE]: {
    0: 'limitation.labels.excNoValue',
    1: 'limitation.labels.excHaveValue'
  },
  [OPERATE.PREFERRED]: {
    0: 'limitation.labels.preferNoValue',
    1: 'limitation.labels.preferHaveValue'
  },
  [OPERATE.NONPREFERRED]: {
    0: 'limitation.labels.nonpreferNoValue',
    1: 'limitation.labels.nonpreferHaveValue'
  }
} as const;

export type DefaultLimitationInventorySettings = {
  name: string;
  addonFeature?: string;
  hideLimitation?: false;
  hideInMenu?: false;
  ignoreValidateOption?: boolean;
  ignoreAddonFeature?: boolean;
  showWithLimitation?: string[];
  disable?: boolean;
  disableReason?: string;
  title?: string;
  singleSelect: boolean;
  doNotCatchOptions?: boolean;
  cannotChangeOperation?: boolean;
  itemSetting: {
    type: ItemType;
    categorySelectable: boolean;
    readonly: boolean;
    i18nPrefix?: string;
    hideExtra?: boolean;
  };
  i18nPrefix?: string;
  requiredOperate?: string[];
  searchPlaceholder: string;
  supportOperates?: OPERATES[];
  extra?: any;
  sidebarTitleKey?: string;
  needInitExtra?: boolean;
  component: ElementType;
  extraComponentProps?: any;
  cb?:
    | ((operate?: OPERATES) => Promise<SelectOptions[]>)
    | ((operate?: OPERATES) => SelectOptions[])
    | ((operate?: OPERATES) => SelectOptions[] | undefined);
  titleHintPropsGetter?: (
    imitation: Limitation,
    needShow: boolean
  ) => { model: DefaultAsyncSourceSpanModel } | undefined;
  preCommentPropsGetter?: (
    limitation: SelectOptions,
    needShow: boolean
  ) => { model: DefaultAsyncSourceSpanModel } | undefined;
  postCommentPropsGetter?: (
    limitation: SelectOptions,
    needShow: boolean
  ) => { model: DefaultAsyncSourceSpanModel } | undefined;
  customInputModelGetter?: () => AbstractCustomInputComponentModel;
  tipsConfig?: {
    debounceTrigger?: number;
    renderTips: (limitation: SelectOptions) => JSX.Element | undefined;
  };
  validator?: (
    operate: string,
    value: SelectOptions[],
    limitationValue: { [key: string]: Limitation[] }
  ) => string | undefined;
  formatter?: (value: string | number) => string;
  // ECOPD-5255: resolve a chip's outdoor operational status (color/sort/tooltip).
  // 傳整個 option（而非只有 value），固定門市時才能讀到 option.spaceId 對出單台狀態。
  healthGetter?: (option: SelectOptions) => AggregatedSpaceHealth | undefined;
  // ECOPD-5615: 這個選項現在為什麼不能被選（電子紙的機台名額滿了就不能）。
  //
  // 回**原因字串**而不是 boolean：一個點不動又不說話的選項，使用者分不出是名額滿了、
  // 權限不足還是壞掉了。回傳的是**已翻譯好的字**（不是 i18n key），會直接當成 hover
  // 提示顯示。回 undefined 代表可以選。
  //
  // 有原因的選項是灰掉但仍顯示 —— 隱藏起來使用者會以為機台不存在，灰掉才看得出
  // 「有這台、但現在選不了」。選項清單是每次開啟時才從 cb() 取的，所以判斷用
  // getter 注入而不是把狀態寫進 option，避免與各層 option 快取打架。
  //
  // 只給「單台」層級的選項用（門市／機台）。群組型選項（地理分區、行政區、區域特性）
  // 不該灰掉 —— Final Spec 明確要求群組仍可選，拆分時自然只會拿到還有名額的機台。
  optionDisabledGetter?: (value: string | number) => string | undefined;
};

export type HiddenLimitationInventorySettings = OverrideAndNeverOthers<
  DefaultLimitationInventorySettings,
  {
    name: string;
    hideLimitation: true;
    ignoreValidateOption: true;
    ignoreAddonFeature: true;
  }
>;

export type HiddenMenuLimitationInventorySettings = Override<
  DefaultLimitationInventorySettings,
  {
    name: string;
    hideInMenu: true;
  }
>;

export type EmptyLimitationInventorySettings = OverrideAndNeverOthers<
  DefaultLimitationInventorySettings,
  {
    name: 'default';
  }
>;

export type LimitationInventorySettings =
  | DefaultLimitationInventorySettings
  | HiddenMenuLimitationInventorySettings
  | HiddenLimitationInventorySettings
  | EmptyLimitationInventorySettings;
